/**
 * Garantías — garantía postventa de la moto vendida y su calendario de
 * mantenimientos (programa "garantias").
 *
 * Solo funciona con varinicial.usagarantias = 'S' (Configuración >
 * Opciones); apagado, la pantalla lo avisa y el backend no crea nada.
 *
 * Reglas (decisiones del negocio, oct 2026):
 *  - La garantía la respalda el dealer.
 *  - Se crea sola al vender al cliente final (contado o crédito); ventas
 *    x mayor, consignación, refinanciamientos y personales no generan.
 *  - Una política por marca (más la "por defecto"); usadas con sus propios
 *    meses (0 = sin garantía).
 *  - El calendario cuenta desde la venta; el km es informativo.
 *  - Un servicio omitido solo alerta: un gerente decide mantenerla vigente
 *    o marcarla perdida, con motivo.
 *
 * El backend (garantiasbackend.py) entrega los datos crudos; calendario.js
 * deriva fechas y estados.
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { FP, PageHeader, EstadoVacio, useToast } from "components/financepro";
import { useApi } from "lib/api";
import { aIso, fmtFecha } from "./calendario";
import { sectionBox } from "./comunes";
import ConsultaGarantia from "./consultagarantia";
import ReporteMantenimientos from "./reportemantenimientos";
import PoliticasGarantia from "./politicasgarantia";

const TABS = ["Consulta", "Mantenimientos por vencer", "Políticas"];

export default function Garantias() {
  const { post } = useApi();
  const toast = useToast();

  const [tab, setTab] = useState(0);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  // ?chasis=… (enlace desde el expediente del préstamo): abre la consulta
  // con ese chasis buscado.
  const location = useLocation();
  const [busqueda, setBusqueda] = useState(() => {
    const chasis = new URLSearchParams(location.search).get("chasis");
    return chasis ? { texto: chasis, t: Date.now() } : null;
  });

  useEffect(() => {
    post("/api/garantias_config", {}).then(({ ok, data }) => {
      if (ok) setConfig(data);
      else setError(data?.Error || "No se pudo leer el módulo de garantías");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verGarantia = (chasis) => {
    setBusqueda({ texto: chasis, t: Date.now() });
    setTab(0);
  };

  let contenido;
  if (error) {
    contenido = (
      <Box sx={sectionBox}>
        <EstadoVacio titulo="No disponible" subtitulo={error} />
      </Box>
    );
  } else if (!config) {
    contenido = (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    );
  } else if (!config.activo) {
    contenido = (
      <Box sx={sectionBox}>
        <EstadoVacio
          icono={VerifiedOutlinedIcon}
          titulo="La garantía no está activada"
          subtitulo='Actívela en Configuración → Opciones → "Usa garantías". Desde ese día cada venta al cliente final genera su garantía.'
        />
      </Box>
    );
  } else {
    const hoy = config.hoy || aIso(new Date());
    contenido = (
      <>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          // zIndex 0: el tema da z-index 10 al flexContainer de las Tabs y,
          // sin contexto propio, pintaba encima del PageHeader sticky.
          sx={{ mb: 2.5, zIndex: 0, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
        >
          {TABS.map((t) => (
            <Tab key={t} label={t} />
          ))}
        </Tabs>

        {tab === 0 ? (
          <ConsultaGarantia config={config} hoy={hoy} busquedaInicial={busqueda} toast={toast} />
        ) : null}
        {tab === 1 ? (
          <ReporteMantenimientos config={config} hoy={hoy} onVer={verGarantia} toast={toast} />
        ) : null}
        {tab === 2 ? <PoliticasGarantia config={config} toast={toast} /> : null}

        {config.activadoDesde ? (
          <Typography sx={{ fontSize: "0.75rem", color: FP.color.textMuted, mt: 3 }}>
            Garantías activas desde el {fmtFecha(config.activadoDesde)}.
          </Typography>
        ) : null}
      </>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader title="Garantías" subtitle="Garantía postventa y mantenimientos" />
      <Box sx={{ p: { xs: 2, md: 3 } }}>{contenido}</Box>
    </Box>
  );
}
